import { type FC } from 'react';
import { Button, Form } from 'react-bootstrap';
import { BsPlus } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import * as uuid from 'uuid';

import { updateElement } from '@editor2/reducers/editor/editorSlice';
import {
	commonPayload,
	isExactMatchElementValidation
} from '@editor2/services/elementValidationsService';
import { updateValidation } from '@editor2/services/elementValidationsActionsService';
import { ValidationRejoinders } from './ValidationRejoinders';
import ConfigCard from './ConfigCard';
import { EnableSwitch } from './EnableSwitch';
import { InfoTooltip } from './InfoTooltip';
import { type RootState } from '@editor2/reducers/root';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { type ResponseBoard, type SAQuestion } from '@editor2/types/pageElements';

interface Props {
	element: SAQuestion | ResponseBoard;
	courseId: number;
}

export const MutableExactMatchValidations: FC<Props> = ({ element, courseId }) => {
	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const { exactMatchValidation } = useSelector(
		(state: RootState) => state.courses[courseId].defaults
	);

	const addExactMatchValidation = () => {
		const validations = [
			...element.validations,
			{
				clientId: uuid.v4(),
				name: 'exact-match',
				enabled: false,
				parameters: {},
				rejoinders: exactMatchValidation.rejoinders,
				element_override: true,
				source: 'element'
			}
		];

		dispatch(updateElement({ ...commonPayload(element, courseId), validations }));
	};

	const removeExactMatch = (index: number) => {
		const validations = [...element.validations];
		validations.splice(index, 1);
		return updateElement({ ...commonPayload(element, courseId), validations });
	};

	return (
		<>
			{element.validations.map((validation, index) => {
				return (
					isExactMatchElementValidation(validation) && (
						<ConfigCard
							removable={true}
							remove={() => {
								dispatch(removeExactMatch(index));
							}}
							key={`element-validation-${validation.clientId}`}
							title={validation.parameters?.['match-expression']}
							collapsed={false}
							enabled={validation.enabled}>
							<div className="parametersBlock">
								<div className="match-expression">
									{!validation.enabled && (
										<div className="match-expression-enabled">
											<span>[N/A]</span>
											<EnableSwitch
												element={element}
												validation={validation}
												validationIndex={index}
												courseId={courseId}
											/>
										</div>
									)}
									{validation.enabled && (
										<>
											<div className="match-expression-enabled">
												<Form.Label className="rejoinderHeader">
													Match Expression <InfoTooltip validationName="match-expression" />
												</Form.Label>
												<EnableSwitch
													element={element}
													validation={validation}
													validationIndex={index}
													courseId={courseId}
												/>
											</div>
											<Form.Group className="match-config">
												<Form.Control
													as="textarea"
													placeholder="Expression"
													className="rejoinder-text"
													rows={1}
													defaultValue={validation.parameters?.['match-expression']}
													// Having a key ensures that we retain focus when re-rendering
													// after the debounced dispatch. The length here is to force the
													// form field to re-render itself so that the correct text shows
													// if we delete a validation.
													key={`${element.validations?.length}-${index}`}
													onChange={(e) =>
														debouncedDispatch(
															updateValidation(
																element,
																{
																	...validation,
																	parameters: {
																		...validation.parameters,
																		...{ 'match-expression': e.target.value }
																	}
																},
																index,
																courseId
															)
														)
													}
												/>
											</Form.Group>
											<div className="exact-match-boolean-parameters">
												<Form.Check
													id={uuid.v4()}
													type="switch"
													checked={validation.parameters?.['partial-match']}
													onChange={() =>
														debouncedDispatch(
															updateValidation(
																element,
																{
																	...validation,
																	parameters: {
																		...validation.parameters,
																		...{
																			'partial-match': !validation.parameters['partial-match']
																		}
																	}
																},
																index,
																courseId
															)
														)
													}
													label={
														<span>
															Partial Match <InfoTooltip validationName="partial-match" />
														</span>
													}
													className="enableSwitch"
												/>

												<Form.Check
													id={uuid.v4()}
													type="switch"
													checked={validation.parameters?.['reject']}
													onChange={() =>
														debouncedDispatch(
															updateValidation(
																element,
																{
																	...validation,
																	parameters: {
																		...validation.parameters,
																		...{ reject: !validation.parameters['reject'] }
																	}
																},
																index,
																courseId
															)
														)
													}
													label={
														<span>
															Reject <InfoTooltip validationName="reject-match" />
														</span>
													}
													className="enableSwitch"
												/>
											</div>{' '}
										</>
									)}
								</div>
							</div>

							<ValidationRejoinders
								element={element}
								validation={validation}
								validationIndex={index}
								courseId={courseId}
							/>
						</ConfigCard>
					)
				);
			})}
			<div className="parametersBlock">
				<Button
					size="sm"
					variant="outline-secondary"
					onClick={() => addExactMatchValidation()}
					className="addRejoinderButton">
					<BsPlus />
					Add another exact match rule
				</Button>
			</div>
		</>
	);
};
